import { Component } from "react"
import PropTypes from "prop-types"
import { map } from "lodash"
import { CardContent, Paper, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material"
import { DATE } from "helpers/shapes"
import { smallPercentageFormatter, oneDecimalPercentageFormatter, percentageFormatter } from "helpers/formatters/numbers"
import { formatLongDate } from "helpers/formatters/dates"
import { computeCSVData } from "helpers/csv"
import MultiLine from "components/charts/multi-line"
import MultiLineBreakdown from "components/charts/multi-line-breakdown"
import Tooltip from "components/analyze-tooltip"
import BreakdownTooltip from "components/breakdown-tooltip"
import TopAttribution from "components/top-attribution"
import GraphTopBar from "components/graph-top-bar"
import ExcelDownload from "components/excel-download"
import DetailRow from "./components/detail-row"

export default class SecurityProfileRisk extends Component {
  static propTypes = {
    startDate: DATE,
    endDate: DATE,
    selectedDate: DATE,
    data: PropTypes.array.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      disabled: PropTypes.bool.isRequired,
    })).isRequired,
    displayType: PropTypes.string.isRequired,
    setDate: PropTypes.func.isRequired,
    setDisplayType: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    focusChartLine: PropTypes.func.isRequired,
    blurChartLine: PropTypes.func.isRequired,
    resetDate: PropTypes.func.isRequired,
    focusedChartLine: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      delta: PropTypes.number.isRequired,
    })).isRequired,
    firstDate: PropTypes.string.isRequired,
    openFactorProfile: PropTypes.func.isRequired,
    details: PropTypes.array.isRequired,
    riskAttribution: PropTypes.object.isRequired,
    maxFactors: PropTypes.number.isRequired,
    toggleOnly: PropTypes.func.isRequired,
    only: PropTypes.string,
    trackCSVDownload: PropTypes.func.isRequired,
    fileName: PropTypes.string.isRequired,
  }

  componentDidUpdate() {
    const { firstDate, startDate: propsStartDate, selectedDate } = this.props
    const startDate = firstDate || propsStartDate

    if (selectedDate && (selectedDate < startDate)) {
      this.props.resetDate()
    }
  }

  onTabChanged = (_, id) => {
    const { setDisplayType } = this.props
    setDisplayType(id)
  }

  render() {
    const {
      setDate,
      endDate,
      selectedDate,
      startDate,
      data,
      items,
      displayType,
      options,
      toggle,
      focusChartLine,
      blurChartLine,
      focusedChartLine,
      details,
      openFactorProfile,
      riskAttribution,
      maxFactors,
      toggleOnly,
      only,
      fileName,
      trackCSVDownload,
    } = this.props

    return <CardContent className="card-content">
      <Paper className="content">
        <div className="graph">
          <GraphTopBar {...{
            value: displayType,
            tabs: map(options, ({ id, label, value }) => ({
              key: id,
              className: id === "total" ? "total-risk" : "",
              value: id,
              label: <><div>{label}</div><div>{percentageFormatter(value)}</div></>,
            })),
            onTabChanged: this.onTabChanged,
            actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items, { percents: true })} fileName={fileName} track={trackCSVDownload} />],
          }}
          />
          {displayType === "total"
            ? <MultiLineBreakdown
              tooltip={<BreakdownTooltip data={data} items={items} formatter={smallPercentageFormatter} />}
              {...{
                  tickFormat: oneDecimalPercentageFormatter,
                  startDate,
                  date: selectedDate,
                  setDate,
                  endDate,
                  data,
                  onlyItem: only,
                  totalLabel: "Total Risk",
                  breakdownLabel: "Risk Decomposition",
                  items,
                  toggle,
                  focusChartLine,
                  blurChartLine,
                  focusedChartLine,
                  toggleOnly,
                }}
            />
            : <MultiLine
              tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={smallPercentageFormatter} />}
              {...{
                  tickFormat: oneDecimalPercentageFormatter,
                  startDate,
                  date: selectedDate,
                  onlyItem: only,
                  setDate,
                  endDate,
                  data,
                  items,
                  toggle,
                  focusChartLine,
                  blurChartLine,
                  focusedChartLine,
                  toggleOnly,
                }}
            />}
        </div>
        <div className="details">
          <h4 className="header">As of {formatLongDate(selectedDate)}</h4>
          {displayType === "total"
            && <Table size="small" style={{ marginBottom: "3rem" }}>
              <TableHead>
                <TableRow>
                  <TableCell width="75%" />
                  <TableCell align="right">% Risk</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <DetailRow value={details[0]} index={0} openFactorProfile={openFactorProfile} />
              </TableBody>
            </Table>}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="75%" />
                <TableCell align="right">% Risk Decomp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {map(details, (value, index) => {
                if (displayType === "total" && index === 0) {
                  return null
                } else {
                  return <DetailRow key={value.id} value={value} index={index} openFactorProfile={openFactorProfile} />
                }
              })}
            </TableBody>
          </Table>
        </div>
      </Paper>
      <TopAttribution date={selectedDate} attribution={riskAttribution} maxFactors={maxFactors} openFactorProfile={openFactorProfile} />
    </CardContent>
  }
}
